import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { 
  Search, User, Building2, Phone, Mail, Home, 
  Loader2, X, Users
} from "lucide-react";
import { API } from "@/App";

/**
 * ApplicantSearch Component
 * Quick search across all applicants by name, email, phone, or address
 * Can be used as a modal or inline component
 */
export function ApplicantSearch({ 
  open, 
  onOpenChange, 
  onSelect,
  mode = "modal", // "modal" or "inline"
  placeholder = "Search applicants...",
  showViewButton = true
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const inputRef = useRef(null);
  
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem("recentApplicantSearches");
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    const searchApplicants = async () => {
      if (!query || query.length < 2) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const res = await axios.get(`${API}/applicants/search`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: query, limit: 10 }
        });
        setResults(res.data.results || []);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchApplicants, 300);
    return () => clearTimeout(debounce);
  }, [query, token]);

  const handleSelect = (applicant) => {
    // Save to recent searches
    const recent = [applicant, ...recentSearches.filter(r => r.applicant_id !== applicant.applicant_id)].slice(0, 5);
    setRecentSearches(recent);
    localStorage.setItem("recentApplicantSearches", JSON.stringify(recent));
    
    if (onSelect) {
      onSelect(applicant);
    }
    
    if (onOpenChange) {
      onOpenChange(false);
    }
    
    setQuery("");
  };

  const handleViewDetails = (applicant) => {
    handleSelect(applicant);
    navigate(`/sales/applicant/${applicant.applicant_id}`);
  };

  const getDisplayName = (applicant) => {
    if (applicant.is_business && applicant.business_name) {
      return applicant.business_name;
    }
    return applicant.display_name || "Unknown";
  };

  const SearchContent = () => (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10 pr-10"
          data-testid="applicant-search-input"
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setQuery("")}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && query.length >= 2 && results.length === 0 && (
        <div className="text-center py-6 text-muted-foreground">
          <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p>No applicants found for "{query}"</p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="divide-y max-h-[400px] overflow-y-auto">
          {results.map((applicant) => (
            <div
              key={applicant.applicant_id}
              className="p-3 hover:bg-slate-50 cursor-pointer transition-colors"
              onClick={() => handleSelect(applicant)}
              data-testid={`search-result-${applicant.applicant_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  {applicant.is_business ? (
                    <Building2 className="w-5 h-5 text-slate-600" />
                  ) : (
                    <User className="w-5 h-5 text-slate-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{getDisplayName(applicant)}</p>
                    <Badge variant="outline" className="capitalize text-xs">
                      {applicant.role}
                    </Badge>
                  </div>
                  
                  {applicant.primary_email && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3" />
                      {applicant.primary_email}
                    </p>
                  )}
                  
                  {applicant.primary_phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {applicant.primary_phone}
                    </p>
                  )}
                  
                  {applicant.case_address && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Home className="w-3 h-3" />
                      {applicant.case_address}
                    </p>
                  )}
                </div>
                
                {showViewButton && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(applicant);
                    }}
                    data-testid={`view-applicant-${applicant.applicant_id}`}
                  >
                    View
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Searches */}
      {!loading && !query && recentSearches.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground px-1 mb-2">Recent Searches</p>
          <div className="divide-y">
            {recentSearches.map((applicant) => (
              <div
                key={applicant.applicant_id}
                className="p-2 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-2"
                onClick={() => handleSelect(applicant)}
              >
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm">{getDisplayName(applicant)}</span>
                <Badge variant="outline" className="text-xs capitalize">
                  {applicant.role}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no query */}
      {!loading && !query && recentSearches.length === 0 && (
        <div className="text-center py-6 text-muted-foreground">
          <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Search by name, email, phone, or address</p>
        </div>
      )}
    </div>
  );

  if (mode === "inline") {
    return <SearchContent />;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Search Applicants
          </DialogTitle>
        </DialogHeader>
        <SearchContent />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Trigger button for opening the search modal
 */
export function ApplicantSearchTrigger({ onClick, className = "" }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={`gap-2 ${className}`}
      data-testid="applicant-search-trigger"
    >
      <Search className="w-4 h-4" />
      Search Applicants
    </Button>
  );
}

export default ApplicantSearch;
